import type { BezierPath, Color } from 'ag-psd';
import { scalePaths, uniformEffectScale } from './vector-geometry';

export type AdjustmentColor = Color;
export interface AdjustmentBinaryResource {
  readonly id: string;
  readonly kind: 'lut' | 'iccProfile';
  readonly data?: string;
  readonly fileId?: string;
  readonly url?: string;
  readonly mimeType?: string;
  readonly fileName?: string;
}
export interface AdjustmentLevelsChannel {
  readonly shadowInput: number;
  readonly highlightInput: number;
  readonly shadowOutput: number;
  readonly highlightOutput: number;
  readonly gamma: number;
}
export interface AdjustmentHueRange {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly hue: number;
  readonly saturation: number;
  readonly lightness: number;
}
export interface AdjustmentBalanceValues {
  readonly cyanRed: number;
  readonly magentaGreen: number;
  readonly yellowBlue: number;
}
export interface AdjustmentMixerChannel {
  readonly black?: number;
  readonly red: number;
  readonly green: number;
  readonly blue: number;
  readonly constant: number;
}
export interface AdjustmentCmyk {
  readonly c: number;
  readonly m: number;
  readonly y: number;
  readonly k: number;
}
export interface AdjustmentCommon {
  readonly sourceVersion?: number;
  readonly enabled?: boolean;
  /** Legacy rectangle scope only; clipBaseBoxId identifies an actual layer silhouette. */
  readonly clipped?: boolean;
  readonly clipBaseBoxId?: string;
  readonly sourceGroupId?: string;
  readonly sourceGroupPassThrough?: boolean;
  readonly clipBasePreserveStaticMask?: boolean;
  readonly colorMode?: string;
  readonly bitDepth?: 8 | 16 | 32;
  readonly fillOpacity?: number;
  readonly documentProfileResourceId?: string;
  readonly presetKind?: number;
  readonly presetFileName?: string;
  readonly maskDataUrl?: string;
  readonly maskUrl?: string;
  readonly maskFileId?: string;
  readonly maskRect?: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
  readonly maskDensity?: number;
  readonly maskDefault?: number;
  readonly maskFeather?: number;
  readonly maskInvert?: boolean;
  readonly maskDisabled?: boolean;
  readonly maskPositionRelativeToLayer?: boolean;
  readonly maskVector?: {
    readonly paths: readonly BezierPath[];
    readonly fillRule?: 'nonzero' | 'evenodd';
    readonly invert?: boolean;
    readonly disabled?: boolean;
    readonly linked?: boolean;
    readonly fillStartsWithAllPixels?: boolean;
    readonly density?: number;
    readonly feather?: number;
  };
  /** Existing serialized fields keep their original units (curve points 0..255; hue degrees, saturation/lightness percent). */
  readonly curveRgb?: readonly (readonly [number, number])[];
  readonly curveRed?: readonly (readonly [number, number])[];
  readonly curveGreen?: readonly (readonly [number, number])[];
  readonly curveBlue?: readonly (readonly [number, number])[];
  readonly hue?: number;
  readonly saturation?: number;
  readonly lightness?: number;
}
export type AdjustmentBoxParams = AdjustmentCommon &
  (
    | {
        readonly kind: 'curves';
        readonly pencil?: boolean;
        readonly curveChannels?: readonly {
          readonly channel: number;
          readonly points?: readonly (readonly [number, number])[];
          readonly map?: readonly number[];
        }[];
      }
    | {
        readonly kind: 'hueSaturation';
        readonly colorize?: boolean;
        readonly colorizeHue?: number;
        readonly colorizeSaturation?: number;
        readonly colorizeLightness?: number;
        readonly ranges?: Partial<Record<'reds' | 'yellows' | 'greens' | 'cyans' | 'blues' | 'magentas', AdjustmentHueRange>>;
      }
    | {
        readonly kind: 'brightness';
        readonly brightness?: number;
        readonly contrast?: number;
        readonly useLegacy?: boolean;
        readonly meanValue?: number;
        readonly labColorOnly?: boolean;
        readonly auto?: boolean;
      }
    | {
        readonly kind: 'levels';
        readonly levelsRgb?: AdjustmentLevelsChannel;
        readonly levelsRed?: AdjustmentLevelsChannel;
        readonly levelsGreen?: AdjustmentLevelsChannel;
        readonly levelsBlue?: AdjustmentLevelsChannel;
        readonly levelChannels?: readonly (AdjustmentLevelsChannel & { readonly channel: number })[];
      }
    | { readonly kind: 'exposure'; readonly exposure?: number; readonly offset?: number; readonly gamma?: number }
    | { readonly kind: 'vibrance'; readonly vibrance?: number }
    | {
        readonly kind: 'colorBalance';
        readonly shadows?: AdjustmentBalanceValues;
        readonly midtones?: AdjustmentBalanceValues;
        readonly highlights?: AdjustmentBalanceValues;
        readonly preserveLuminosity?: boolean;
      }
    | {
        readonly kind: 'blackWhite';
        readonly reds?: number;
        readonly yellows?: number;
        readonly greens?: number;
        readonly cyans?: number;
        readonly blues?: number;
        readonly magentas?: number;
        readonly useTint?: boolean;
        readonly tintColor?: AdjustmentColor;
      }
    | {
        readonly kind: 'photoFilter';
        readonly xyz?: readonly [number, number, number];
        readonly color?: AdjustmentColor;
        readonly density?: number;
        readonly preserveLuminosity?: boolean;
      }
    | {
        readonly kind: 'channelMixer';
        readonly monochrome?: boolean;
        readonly red?: AdjustmentMixerChannel;
        readonly green?: AdjustmentMixerChannel;
        readonly blue?: AdjustmentMixerChannel;
        readonly gray?: AdjustmentMixerChannel;
      }
    | {
        readonly kind: 'colorLookup';
        readonly lookupType?: '3dlut' | 'abstractProfile' | 'deviceLinkProfile';
        readonly name?: string;
        readonly dither?: boolean;
        readonly lutFormat?: 'look' | 'cube' | '3dl';
        readonly lut3DFileName?: string;
        readonly dataOrder?: 'rgb' | 'bgr';
        readonly tableOrder?: 'rgb' | 'bgr';
        readonly lutResourceId?: string;
        readonly profileResourceId?: string;
        readonly domainMin?: readonly number[];
        readonly domainMax?: readonly number[];
        readonly interpolation?: 'trilinear' | 'tetrahedral';
      }
    | { readonly kind: 'invert' }
    | { readonly kind: 'posterize'; readonly posterizeLevels?: number }
    | { readonly kind: 'threshold'; readonly thresholdLevel?: number }
    | {
        readonly kind: 'gradientMap';
        readonly name?: string;
        readonly gradientType?: 'solid' | 'noise';
        readonly dither?: boolean;
        readonly reverse?: boolean;
        readonly method?: 'classic' | 'perceptual' | 'linear' | 'smooth';
        readonly smoothness?: number;
        readonly colorStops?: readonly {
          readonly color: AdjustmentColor;
          readonly location: number;
          readonly midpoint: number;
        }[];
        readonly opacityStops?: readonly { readonly opacity: number; readonly location: number; readonly midpoint: number }[];
        readonly roughness?: number;
        readonly colorModel?: 'rgb' | 'hsb' | 'lab';
        readonly randomSeed?: number;
        readonly restrictColors?: boolean;
        readonly addTransparency?: boolean;
        readonly min?: readonly number[];
        readonly max?: readonly number[];
      }
    | {
        readonly kind: 'selectiveColor';
        readonly mode?: 'relative' | 'absolute';
        readonly reds?: AdjustmentCmyk;
        readonly yellows?: AdjustmentCmyk;
        readonly greens?: AdjustmentCmyk;
        readonly cyans?: AdjustmentCmyk;
        readonly blues?: AdjustmentCmyk;
        readonly magentas?: AdjustmentCmyk;
        readonly whites?: AdjustmentCmyk;
        readonly neutrals?: AdjustmentCmyk;
        readonly blacks?: AdjustmentCmyk;
      }
  );

export function adjustmentResourceIds(params: AdjustmentBoxParams): string[] {
  return [
    params.documentProfileResourceId,
    ...(params.kind === 'colorLookup' ? [params.lutResourceId, params.profileResourceId] : []),
  ].filter((id): id is string => !!id);
}

export function transformAdjustmentMask(
  params: AdjustmentBoxParams,
  sx: number,
  sy: number,
  ox: number,
  oy: number,
): AdjustmentBoxParams {
  const k = uniformEffectScale(sx, sy);
  return {
    ...params,
    ...(params.maskRect && {
      maskRect: {
        x: params.maskRect.x * sx + ox,
        y: params.maskRect.y * sy + oy,
        width: params.maskRect.width * sx,
        height: params.maskRect.height * sy,
      },
    }),
    ...(params.maskFeather != null && { maskFeather: params.maskFeather * k }),
    ...(params.maskVector && {
      maskVector: {
        ...params.maskVector,
        paths: scalePaths(params.maskVector.paths, sx, sy, ox, oy),
        ...(params.maskVector.feather != null && { feather: params.maskVector.feather * k }),
      },
    }),
  };
}
